<template>
  <div>
    <themed-form
      :fields="formFields"
      :model-value="form"
      :errors="formErrors"
      layout="grid"
      :columns="6"
      :validate-on-change="true"
      @input="handleInput"
      @submit="submit"
    >
      <template #submit>
        <div class="flex justify-between mt-6 col-span-6">
          <themed-button
            type="button"
            variant="outline"
            @click="goToLogin"
          >
            Back to Login
          </themed-button>
          <themed-button
            type="submit"
            variant="default"
            :disabled="loading"
          >
            <span v-if="loading" class="loader mr-2"></span>
            {{ loading ? 'Please wait...' : (client && user ? 'Update and Continue' : 'Continue') }}
          </themed-button>
        </div>
      </template>
    </themed-form>
  </div>
</template>

<script>
import axios from 'axios';
import { formatCellNumber } from '../../../../utils/formatters';

export default {
  name: 'SignupSetup',
  props: {
    formData: Object,
    client: Object,
    user: Object,
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        email: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: 16,
        password: '',
        password_confirmation: '',
        cell: '',
      },
      formErrors: {},
      countries: [],
      states: [],
    }
  },
  computed: {
    formFields() {
      const fields = [
        {
          name: 'name',
          type: 'input',
          inputType: 'text',
          label: 'Name',
          required: true,
          col: 2,
          validation: {
            required: true
          }
        },
        {
          name: 'email',
          type: 'input',
          inputType: 'email',
          label: 'Email',
          required: true,
          col: 2,
          componentProps: {
            readonly: !!(this.client && this.user)
          },
          validation: {
            required: true,
            email: true
          }
        },
        {
          name: 'cell',
          type: 'input',
          inputType: 'text',
          label: 'Cell',
          required: true,
          col: 2,
          validation: {
            required: true,
            pattern: /^\+1[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
            message: 'Enter cell with country code (e.g., +1-555-555-5555).'
          }
        },
        {
          name: 'address',
          type: 'input',
          inputType: 'text',
          label: 'Address',
          required: true,
          col: 6,
          validation: {
            required: true
          }
        },
        {
          name: 'city',
          type: 'input',
          inputType: 'text',
          label: 'City',
          required: true,
          col: 2,
          validation: {
            required: true
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          required: true,
          col: 2,
          options: this.states,
          componentProps: {
            'option-label': 'name',
            'option-value': 'state'
          },
          validation: {
            required: true
          }
        },
        {
          name: 'postal_code',
          type: 'input',
          inputType: 'text',
          label: 'Postal Code',
          required: true,
          col: 1,
          componentProps: {
            maxlength: 15
          },
          validation: {
            required: true
          }
        },
        {
          name: 'country_id',
          type: 'select',
          label: 'Country',
          required: true,
          col: 1,
          options: this.countries,
          componentProps: {
            'option-label': 'name',
            'option-value': 'id'
          },
          validation: {
            required: true
          }
        }
      ];

      // Add password fields only for new signups
      if (!this.client || !this.user) {
        fields.push(
          {
            name: 'password',
            type: 'input',
            inputType: 'password',
            label: 'Password',
            required: true,
            col: 3,
            validation: {
              required: true,
              minLength: 6,
              message: 'Password must be at least 6 characters.'
            }
          },
          {
            name: 'password_confirmation',
            type: 'input',
            inputType: 'password',
            label: 'Confirm Password',
            required: true,
            col: 3,
            validation: {
              required: true,
              matchField: 'password',
              message: 'Passwords do not match.'
            }
          }
        );
      }

      return fields;
    }
  },
  mounted() {
    axios.get('/api/address-info').then((response) => {
      this.countries = response.data.countries;
      this.states = response.data.states;
    });
    if (this.formData) {
      this.form = { ...this.form, ...this.formData };
    } else if (this.client && this.user) {
      this.form = {
        ...this.form,
        name: this.user.name,
        email: this.user.email,
        address: this.client.address,
        city: this.client.city,
        state: this.client.state,
        postal_code: this.client.postal_code,
        country_id: this.client.country_id,
        cell: this.formatCellNumber(this.user.cell)
      };
    }
  },
  methods: {
    formatCellNumber,
    goToLogin() {
      // Redirect to login (will logout if needed)
      window.location.href = '/signup/back-to-login';
    },
    handleInput(data) {
      // Format cell number on input
      if (data.cell && data.cell !== this.form.cell) {
        data.cell = this.formatCellNumber(data.cell);
      }
      this.form = data;
    },
    getFirstErrorMessage(errorsObj, fallback = 'Please check the form for errors.') {
      for (const key in errorsObj) {
        if (Array.isArray(errorsObj[key]) && errorsObj[key].length > 0) return errorsObj[key][0];
        if (typeof errorsObj[key] === 'object' && errorsObj[key] !== null) {
          const nested = this.getFirstErrorMessage(errorsObj[key]);
          if (nested) return nested;
        }
      }
      return fallback;
    },
    async submit() {
      this.formErrors = {};
      this.loading = true;

      const isUpdate = !!(this.client && this.user);
      
      const payload = {
        client: {
          name: this.form.name,
          address: this.form.address,
          city: this.form.city,
          state: this.form.state,
          postal_code: this.form.postal_code,
          country_id: parseInt(this.form.country_id, 10)
        },
        user: {
          name: this.form.name,
          email: this.form.email,
          cell: this.form.cell,
          country_id: parseInt(this.form.country_id, 10)
        }
      };
      
      if (isUpdate) {
        payload.client_id = this.client.id;
        payload.user_id = this.user.id;
      } else {
        payload.user.password = this.form.password;
        payload.user.password_confirmation = this.form.password_confirmation;
      }
      
      try {
        const url = isUpdate ? '/signup/update' : '/signup';
        const resp = await axios.post(url, payload, { validateStatus: () => true });
        
        if (resp.data.errors) {
          this.formErrors = resp.data.errors;
          if (resp.data.errors['user.email']) {
            this.$notify({
              type: 'error',
              title: 'Email Exists',
              text: 'This email is already registered. Please log in or use another email.',
              duration: 2000
            });
            this.loading = false;
            return;
          }
          this.$notify({
            type: 'error',
            title: 'Error',
            text: this.getFirstErrorMessage(resp.data.errors)
          });
          this.loading = false;
          return;
        }
        
        if (resp.data.client && resp.data.user) {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: resp.data.message || (isUpdate ? 'Update successful!' : 'Signup successful!')
          });
          this.$emit('signup-setup-complete', { client: resp.data.client, user: resp.data.user, formData: { ...this.form } });
        } else if (resp.data.message && resp.status !== 201) {
          this.formErrors = { general: [resp.data.message] };
          this.$notify({
            type: 'error',
            title: 'Error',
            text: resp.data.message
          });
          this.loading = false;
          return;
        }
      } catch (error) {
        this.formErrors = error.response?.data?.errors || {};
        this.$notify({
          type: 'error',
          title: 'Error',
          text: this.getFirstErrorMessage(this.formErrors, 'An unexpected error occurred.')
        });
        this.loading = false; 
      }
    },
  }
}
</script>

<style>
.loader {
  border: 2px solid #fff;
  border-top: 2px solid var(--primary);
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  vertical-align: middle;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
